import { useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ProtectedRoute, FeatureRoute } from "./utils/roleGuard";
import { ANALYST_ROLES } from "./constants/roles";
import { ADMIN_ROLES } from "./constants/roles";
import Sidebar from "./components/Layout/Sidebar";
import TopBar from "./components/Layout/TopBar";
import OnboardingWizard from "./components/Onboarding/OnboardingWizard";
import TwoFAGraceNotice from "./components/TwoFAGraceNotice";
import { useAuth } from "./hooks/useAuth";
import { getGreeting } from "./utils";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DemoLandingPage from "./pages/DemoLandingPage";

const IS_DEMO = import.meta.env.VITE_APP_ENV === "demo";
import ChatPage from "./pages/ChatPage";
import AdminPage from "./pages/AdminPage";
import AdminUserEditPage from "./pages/AdminUserEditPage";
import AdminInviteUserPage from "./pages/AdminInviteUserPage";
import ReportsPage from "./pages/ReportsPage";
import DashboardPage from "./pages/DashboardPage";
import ScheduledDashboardsPage from "./pages/ScheduledDashboardsPage";
import CustomAlertsPage from "./pages/CustomAlertsPage";
import ServiceStatusPage from "./pages/ServiceStatusPage";
import IncidentDetailPage from "./pages/IncidentDetailPage";
import IncidentPostmortemPage from "./pages/IncidentPostmortemPage";
import ChangelogPage from "./pages/ChangelogPage";
import SettingsPage from "./pages/SettingsPage";
import NotFoundPage from "./pages/NotFoundPage";

function AppLayout({ title, showGreeting, children }: { title: string; showGreeting?: boolean; children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, refreshUser } = useAuth();
  const firstName = user?.full_name?.split(" ")[0] ?? user?.email?.split("@")[0] ?? "";
  const displayTitle = showGreeting ? `${getGreeting()}${firstName ? `, ${firstName}` : ""}` : title;
  const navigate = useNavigate();

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar title={displayTitle} onMenuClick={() => setSidebarOpen(true)} />
        {user && (
          <TwoFAGraceNotice
            user={user}
            onGoToSettings={() => navigate("/settings?tab=security")}
          />
        )}
        {IS_DEMO && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-center text-xs text-amber-800">
            Demo mode — all data is synthetic and does not represent real activity.
          </div>
        )}
        <main className="flex-1 overflow-auto">{children}</main>
      </div>

      {user && !user.onboarding_completed && (
        <OnboardingWizard
          agentName={user.agent_name ?? "Brian"}
          onComplete={refreshUser}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
        <Routes>
          <Route path="/demo" element={<DemoLandingPage />} />
          <Route path="/login" element={IS_DEMO ? <Navigate to="/demo" replace /> : <LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/chat"
            element={
              <FeatureRoute feature="chat">
                <AppLayout title="Chat">
                  <ChatPage />
                </AppLayout>
              </FeatureRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <FeatureRoute feature="reports_view">
                <AppLayout title="Reports">
                  <ReportsPage />
                </AppLayout>
              </FeatureRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout title="" showGreeting>
                  <DashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduled-dashboards"
            element={
              <ProtectedRoute roles={ANALYST_ROLES}>
                <AppLayout title="Scheduled Dashboards">
                  <ScheduledDashboardsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-alerts"
            element={
              <ProtectedRoute roles={ANALYST_ROLES}>
                <AppLayout title="Custom Alerts">
                  <CustomAlertsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/status"
            element={
              <FeatureRoute feature="service_status_view">
                <AppLayout title="Service Status">
                  <ServiceStatusPage />
                </AppLayout>
              </FeatureRoute>
            }
          />
          <Route
            path="/status/incidents/:id"
            element={
              <FeatureRoute feature="service_status_view">
                <AppLayout title="Incident">
                  <IncidentDetailPage />
                </AppLayout>
              </FeatureRoute>
            }
          />
          <Route
            path="/status/incidents/:id/post-mortem"
            element={
              <FeatureRoute feature="service_status_view">
                <AppLayout title="Post-mortem">
                  <IncidentPostmortemPage />
                </AppLayout>
              </FeatureRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={ADMIN_ROLES}>
                <AppLayout title="Admin">
                  <AdminPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/invite"
            element={
              <ProtectedRoute roles={ADMIN_ROLES}>
                <AppLayout title="Invite User">
                  <AdminInviteUserPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <ProtectedRoute roles={ADMIN_ROLES}>
                <AppLayout title="Edit User">
                  <AdminUserEditPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/changelog"
            element={
              <ProtectedRoute>
                <AppLayout title="What's New">
                  <ChangelogPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AppLayout title="Settings">
                  <SettingsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
